import React, { useEffect, useState, useRef } from 'react'
import styled from 'styled-components'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'
import { DoorTop } from 'apis/response_types'
import { getDoor } from 'apis/doors'
import { listDoorPlansV1 } from 'apis/door/SubscriptionPlansV1'
import { SubscriptionPlanV1 } from 'apis/response_types'
import { useDispatch } from 'react-redux'
import loadingModules from 'modules/loading'

import { getMe } from 'apis/me'
import { isAxiosError } from 'apis/base'
import doorModules from 'modules/door'
import alertModules from 'modules/alert'
import SelectPlanPhase from './SelectPlanPhase'
import PlanPrice from '../../components/common/PlanPrice'
import dayjs from 'dayjs'
// resources redesin
import IconUser from '../../images/redesign/icon_user.svg'
import SecretPlanHeader from 'images/secret_plan_header.svg'
import DoorLoginModal from 'components/door/DoorLoginModal'
// sampleIamge
import ImageNormal from '../../images/redesign/image_normal.jpg'
import {
  limitedDates,
  LimitedDates,
  monthString,
  displayFormatWithWeekDay,
  addMonthDate
} from 'components/management/subscription_plans/limited_functions'
import { useSearchParams } from 'hooks/useSearchParams'
import classNames from 'classnames'
import OwnerDetail from 'components/door/OwnerDetail'

export const freeTrialText = (freeTrialPeriod: number | null) => {
  let text = ''
  if (freeTrialPeriod === -1) {
    text = '初月無料'
  } else {
    text = `${freeTrialPeriod}日間無料`
  }
  return text
}

export default function SelectDoorPlans() {
  const ref = useRef<HTMLLIElement | null>(null)
  let { door_id }: any = useParams()
  const history = useHistory()
  const [searchParams] = useSearchParams()
  const dispatch = useDispatch()
  const selectId = searchParams.get('select')
  const planSecret = searchParams.get('secret') || undefined
  const roomId = searchParams.get('room')
  const [isOpenLoginModal, setIsOpenLoginModal] = useState(false)
  const [loginCallbackPath, setLoginCallbackPath] = useState<string>('')
  const [response, setResponse] = useState<DoorTop | null>(null)
  const [plans, setPlans] = useState<SubscriptionPlanV1[]>([])
  const [otherPlans, setOtherPlans] = useState<SubscriptionPlanV1[]>([])
  const paragraphRefs = useRef<(HTMLParagraphElement | null)[]>([])
  const [isExpanded, setIsExpanded] = useState<boolean[]>([])
  const [isOverflow, setIsOverflow] = useState<boolean[]>([])

  useEffect(() => {
    const checkAllOverflows = () => {
      const mobileViewportWidth = 768
      const overflowThreshold =
        window.innerWidth <= mobileViewportWidth ? 164 : 216
      const newOverflows = plans.map((_, index) => {
        return (
          (paragraphRefs.current[index]?.clientHeight ?? 0) > overflowThreshold
        )
      })
      setIsOverflow(newOverflows)
    }
    const handleResize = () => {
      checkAllOverflows()
    }
    checkAllOverflows()

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [plans])

  const toggleExpand = (index: number) => {
    setIsExpanded((prevExpands) => {
      const newExpands = [...prevExpands]
      newExpands[index] = !newExpands[index]
      return newExpands
    })
  }

  // 納品データをReact用に調整
  const [data, setData] = useState({
    isSp: false,
    isOpenSearch: false,
    isOpenSpMenu: false,
    isHeaderVisible: false,
    isFixedPlanOwner: false
  })

  const planListUl = useRef<HTMLUListElement>(null)

  const handleScroll = () => {
    const scrollTop = document.documentElement.scrollTop
    let updatedData = { ...data }
    if (scrollTop > 500) {
      updatedData.isHeaderVisible = true
    } else {
      updatedData.isHeaderVisible = false
    }
    if (planListUl.current) {
      const planListUlTop = planListUl.current!.getBoundingClientRect().top
      updatedData.isFixedPlanOwner = !(planListUlTop >= 120)
    }
    setData(updatedData)
  }

  const openLoginModal = (callbackPath: string) => {
    setIsOpenLoginModal(true)
    setLoginCallbackPath(callbackPath)
  }

  const fetchPlan = async () => {
    const planRes = await listDoorPlansV1(door_id, { secret: planSecret })
    const plans = planRes.data
    if (roomId) {
      const plansOfRoom = plans.filter((plan) =>
        plan.rooms.some((room) => room.id === Number(roomId))
      )
      const otherPlans = plans.filter((plan) =>
        plan.rooms.every((room) => room.id !== Number(roomId))
      )
      setPlans(plansOfRoom)
      setOtherPlans(otherPlans)
      if (plansOfRoom.length === 0) {
        dispatch(
          alertModules.actions.setAlert({
            text: '該当のプランは非表示に設定されています',
            severity: 'error'
          })
        )
      }
    } else {
      setPlans(planRes.data)
    }
  }

  const fetchDoor = async () => {
    let res = await getDoor(door_id)
    const door = res.data
    setResponse(res.data)
    return door
  }

  useEffect(() => {
    const getData = async () => {
      try {
        dispatch(loadingModules.actions.start())
        fetchPlan()
        const door = await fetchDoor()
        if (door.door) {
          dispatch(
            doorModules.actions.setCustomize({
              customize: door.door?.customize
            })
          )
        }
      } finally {
        dispatch(loadingModules.actions.stop())
      }
    }
    try {
      dispatch(
        doorModules.actions.setCustomize({
          customize: true
        })
      )
      getData()
    } catch {
      dispatch(
        doorModules.actions.setCustomize({
          customize: false
        })
      )
    }
  }, [])

  // コンポーネントが破棄される際にカスタム表示のフラグを解除する。
  // これをしないとドアページ以外でもカスタム表示の影響が出てしまうため。
  useEffect(() => {
    return () => {
      dispatch(
        doorModules.actions.setCustomize({
          customize: false
        })
      )
    }
  }, [])

  useEffect(() => {
    if (selectId !== null) {
      setTimeout(() => {
        ref?.current?.scrollIntoView({ behavior: 'smooth' })
      }, 50)
    }
  }, [plans])

  const planLiClass = (plan: SubscriptionPlanV1) => {
    if (plan.is_capacity_limit) {
      return 'is-over'
    } else if (plan.is_subscribing || plan.is_suspended || plan.is_reserving) {
      return 'is-disable'
    } else {
      return ''
    }
  }
  const planButtonText = (plan: SubscriptionPlanV1) => {
    let text = ''
    if (plan.is_subscribing) {
      text = 'このプランで参加中'
    } else if (plan.is_suspended) {
      text = '募集停止中'
    } else if (plan.is_capacity_limit) {
      text = '現在定員に達しています'
    } else if (plan.joinable_starts_at !== null) {
      if (dayjs(plan.joinable_starts_at).valueOf() > Date.now()) {
        text = plan.is_reserving
          ? 'このプランに申し込み中'
          : 'このプランに申し込む'
      } else {
        text = 'このプランに参加'
      }
    } else {
      text = 'このプランに参加'
    }
    return text
  }

  const PlanLists = (previewPlans: SubscriptionPlanV1[]) => {
    return previewPlans.map((plan, index) => (
      <li
        className={classNames('plan-list-item', planLiClass(plan), {
          'is-secretPlan': !!plan.secret_id
        })}
        key={plan.id}
        id={`section${plan.id}`}
        ref={plan.id === Number(selectId) ? ref : null}
      >
        {plan.secret_id && (
          <div className="plan-list-secretPlan-header">
            <img src={SecretPlanHeader} alt="SECRET PLAN" />
          </div>
        )}
        <section className="plan-list-upper-box">
          <div
            className={`plan-list-image-box ${
              plan.thumnail_image ? '' : 'plan-image-none'
            }`}
          >
            <div
              className={`${plan.thumnail_image ? 'plan-list-image' : ''}`}
              style={{
                backgroundImage: plan.thumnail_image
                  ? `url(${plan.thumnail_image || ImageNormal})`
                  : 'none'
              }}
            ></div>
          </div>
          <div
            className={`plan-list-title-box ${
              plan.thumnail_image ? '' : 'plan-image-none'
            }`}
          >
            {!plan.is_suspended && (
              <ul className="plan-list-status">
                {(() => {
                  if (plan.is_display_member_count) {
                    let name
                    let count
                    if (plan.reservable) {
                      name = '申し込み数'
                      count = plan.reservation_count
                    } else {
                      name = 'メンバー数'
                      count = plan.member_count
                    }
                    return (
                      <li className="plan-list-status-item">
                        <span
                          className="plan-list-user-icon"
                          style={{
                            backgroundImage: `url(${IconUser})`
                          }}
                        ></span>
                        {name}
                        <span className="plan-list-member-number">{count}</span>
                        <span className="plan-list-text-small">名</span>
                      </li>
                    )
                  }
                })()}
                {(() => {
                  if (plan.capacity !== null) {
                    // member_countはメンバー表示フラグの状態でnullか数値がはいる
                    // nullじゃなく、かつ人数上限に達していない場合のみ定員表示をON
                    return (
                      <>
                        <li className="plan-list-status-item">
                          <span
                            className="plan-list-user-icon"
                            style={{
                              backgroundImage: `url(${IconUser})`
                            }}
                          ></span>
                          定員
                          <span className="plan-list-member-number">
                            {plan.capacity}
                          </span>
                          <span className="plan-list-text-small">名</span>
                        </li>
                      </>
                    )
                  }
                })()}
              </ul>
            )}
            <h3 className="plan-list-title">{plan.title}</h3>
            <p className="plan-list-price">
              <PlanPrice plan={plan} />
            </p>
            <button
              className={`plan-list-button button ${
                plan.is_subscribing ||
                plan.is_suspended ||
                plan.is_capacity_limit ||
                plan.is_reserving
                  ? 'button-disable'
                  : 'button-gra2'
              }`}
              style={{ border: 'none' }}
              onClick={async (event: any) => {
                const confirmPlanSearchParams = new URLSearchParams()
                confirmPlanSearchParams.append('selected_id', String(plan.id))
                if (plan.secret_id && planSecret)
                  confirmPlanSearchParams.append('secret', planSecret)
                const confirmPath = `/door/${door_id}/plan_confirm?${confirmPlanSearchParams.toString()}`
                await getMe()
                  .then(() => {
                    history.push(confirmPath)
                  })
                  .catch((e) => {
                    if (isAxiosError(e)) {
                      switch (e.response?.status) {
                        case 401:
                          openLoginModal(confirmPath)
                          return
                      }
                    }
                    throw e
                  })
              }}
            >
              {planButtonText(plan)}
              {plan.free_trial_period !== null && (plan.is_free_trial_available === true || plan.is_free_trial_available === null) && (
                <span className="button-infotag">
                  {freeTrialText(plan.free_trial_period)}
                </span>
              )}
            </button>
            {plan.free_trial_period !== null &&
              plan.is_free_trial_available === false && (
                <p style={{ color: '#f35d85', fontSize: '11px' }}>
                  ※過去にサロン参加済みのため、無料期間対象外となります
                </p>
              )}
          </div>
        </section>
        <section className={`plan-list-bottom-box`}>
          <div
            className={`${
              isOverflow[index] ? (isExpanded[index] ? '' : 'expanded') : ''
            }`}
          >
            <p
              ref={(el) => (paragraphRefs.current[index] = el)}
              className={`plan-list-paragraph`}
            >
              {plan.description}
            </p>
            {(() => {
              if (
                plan.joinable_starts_at &&
                plan.joinable_months &&
                plan.available_days_after_joinable
              ) {
                const dates: LimitedDates = limitedDates(
                  plan.joinable_starts_at,
                  plan.joinable_months,
                  plan.available_days_after_joinable
                )
                return (
                  <>
                    <div className="plan-list-caution-box">
                      <h4 className="plan-list-caution-title">
                        期間 / お支払いについて
                      </h4>
                      <ul>
                        <li>
                          {dates.start}〜{dates.end}までの
                          {monthString(plan.joinable_months)}
                          の期間限定プランになります
                        </li>
                        <li>
                          プラン開始時に、
                          {Math.round(
                            plan.subscription_cost / plan.joinable_months
                          ).toLocaleString()}
                          円×{monthString(plan.joinable_months)}＝
                          {plan.subscription_cost.toLocaleString()}
                          円（税込）を一括でお支払いいただきます
                        </li>
                        <li>
                          {dates.start_only_dates}
                          より順次課金が実施され、完了した方からプラン開始となります
                        </li>
                        <li>
                          コンテンツの閲覧・視聴期限は
                          {dates.available}
                          までとなり、その後、自動的に退会扱いとなります
                        </li>
                        <li>
                          やむを得ない事情により、開始の予定日が変更・延期となる可能性もございます。予めご了承下さい
                        </li>
                      </ul>
                    </div>
                  </>
                )
              }
            })()}
            {(() => {
              if (!plan.joinable_starts_at) {
                if (plan.recurring) {
                  return (
                    <div className="plan-list-caution-box">
                      <ul>
                        <li>期間毎に自動更新するサブスクプランです、表示期間終了後の翌月1日に決済が自動更新します</li>
                        <li>参加と同時に表示期間分の決済を行い、月の途中の参加でも日割り計算になりません</li>
                      </ul>
                    </div>
                  )
                } else {
                  return (
                    <div className="plan-list-caution-box">
                      <ul>
                        <li>
                          {dayjs().format(displayFormatWithWeekDay)}〜
                          {addMonthDate(
                            dayjs(),
                            plan.payment_interval_months
                          ).format(displayFormatWithWeekDay)}
                          までの{plan.payment_interval_months}
                          ヶ月間のプランになります
                        </li>
                        <li>参加時にまとめてお支払いいただくプランです</li>
                        <li>期間終了後に、自動でプラン退会扱いとなります</li>
                      </ul>
                    </div>
                  )
                }
              }
            })()}
          </div>
          {isOverflow[index] && (
            <button
              onClick={() => toggleExpand(index)}
              className="plan-list-paragraph-more"
            >
              {isExpanded[index] ? '閉じる' : 'もっと見る'}
            </button>
          )}
        </section>
      </li>
    ))
  }

  return (
    <>
      <PlanListWrap>
        <SelectPlanPhase phase={0} isSelectSamplePostRoom={!!roomId} />
        <PlanLayout>
          <div className="layout-col2">
            <div className="layout-col2-left">
              <ul className="plan-list">{PlanLists(plans)}</ul>
              {roomId && (
                <OtherPlansDiv>
                  <h3>その他のプランはこちら</h3>
                  <ul className="plan-list">{PlanLists(otherPlans)}</ul>
                </OtherPlansDiv>
              )}
            </div>
            <OwnerDetail
              ownerName={response ? response.owner.name : ''}
              ownerIcon={response ? response.owner.icon : ''}
              description={
                response && response.door ? response.door.description : ''
              }
              doorId={door_id}
            />
          </div>
        </PlanLayout>
      </PlanListWrap>
      {response?.door && (
        <DoorLoginModal
          door={response.door}
          owner={response.owner}
          isOpen={isOpenLoginModal}
          setIsOpen={setIsOpenLoginModal}
          callbackPath={loginCallbackPath}
        />
      )}
    </>
  )
}
const PlanListWrap = styled.div`
  font-family: 'Noto Sans JP', sans-serif;
  line-height: 1.9;
  font-size: 13px;
  background-color: #ffffff;
  color: #333;
  -webkit-font-smoothing: antialiased;
  font-weight: 500;

  &::-moz-selection {
    color: #ffffff;
    background-color: #26bfdb;
  }
  &::selection {
    color: #ffffff;
    background-color: #26bfdb;
  }
  .text-blue {
    color: #26bfdb;
  }
  .text-pink {
    color: #fc4db9;
  }
  .text-red {
    color: #f03b6b;
  }
  .text-yellow {
    color: #ffff53;
  }
  .text-bold {
    font-weight: bold;
  }
  .tb-only {
    display: none;
  }
  @media (max-width: 768px) {
    .tb-only {
      display: block;
    }
  }
  .pc-only {
    display: block;
  }
  @media (max-width: 768px) {
    .pc-only {
      display: none;
    }
  }

  .button {
    &.button-blue {
      background-color: #26bfdb;
      box-shadow: 0 12px 32px rgba(38, 191, 219, 0.24);
      border: 1px solid #ffffff;
      width: 378px;
      height: 72px;
      border-radius: 36px;
      display: flex;
      color: #ffffff;
      align-items: center;
      font-size: 16px;
      transition: 0.3s ease;
      font-weight: bold;
      &.is-text-center {
        text-align: center;
        .button-icon-arrow {
          padding: 0;
        }
      }
      &:hover {
        background-color: #ffffff;
        color: #26bfdb;
        .button-icon-arrow {
          &::before {
            background-image: url({IconArrowBlue});
          }
        }
      }
    }
    &.button-icon-arrow {
      position: relative;
      display: block;
      text-align: center;
      width: 100%;
      &::before {
        content: '';
        position: absolute;
        top: 9px;
        right: 25px;
        width: 14px;
        height: 14px;
        background-image: url({IconArrow});
        background-repeat: no-repeat;
        background-size: contain;
        transition: 0.3s ease;
      }
    }
    &.button-tag-list {
      display: flex;
      flex-wrap: wrap;
      @media (max-width: 768px) {
        justify-content: center;
      }
    }
    &.button-tag-item {
      margin-bottom: 8px;
      margin-top: 8px;
      margin-left: 7px;
      margin-right: 7px;
      @media (max-width: 768px) {
        margin-bottom: 4px;
        margin-top: 4px;
        margin-left: 2px;
        margin-right: 2px;
      }
    }
    &.button-tag {
      display: inline-block;
      height: 35px;
      border: 1px solid #26bfdb;
      border-radius: 17.5px;
      color: #26bfdb;
      padding: 0 22px;
      line-height: 33px;
      transition: 0.3s ease;
      &:hover {
        background-color: #26bfdb;
        color: #ffffff;
      }
      & + .button-tag {
        margin-left: 12px;
      }
    }
    &.button-white {
      width: 216px;
      height: 55px;
      border-radius: 27.5px;
      background-color: #ffffff;
      display: inline-block;
      line-height: 55px;
      color: #333;
      position: relative;
      font-size: 13px;
      text-align: center;
      outline: 3px solid rgba(255, 255, 255, 0.3);
      transition: 0.3s ease;
      font-weight: bold;
      &:hover {
        background-color: #26bfdb;
        color: #ffffff;
        &.is-arrow {
          &::before {
            background-image: url({IconArrowWhite});
          }
        }
      }
      &.is-arrow {
        &::before {
          content: '';
          position: absolute;
          right: 16px;
          top: 21px;
          width: 8px;
          height: 14px;
          background-size: cover;
          background-image: url({IconArrowBlue2});
        }
      }
    }
    &.button-gra2 {
      width: 100%;
      height: 48px;
      border-radius: 24px;
      background: rgb(25, 152, 208);
      background: linear-gradient(
        140deg,
        rgb(25, 152, 208) 0%,
        rgb(102, 242, 210) 100%
      );
      display: inline-block;
      line-height: 48px;
      color: #ffffff;
      position: relative;
      font-size: 15px;
      text-align: center;
      cursor: pointer;
      transition: 0.3s ease;
      background-color: #26bfdb;
      font-weight: bold;
      &:hover {
        background-color: #ffffff;
        background: #ffffff;
        color: #26bfdb;
        box-shadow: 0 4px 24px rgba(107, 133, 141, 0.16);
      }
      &.is-disable {
        background: #989898;
        pointer-events: none;
      }
    }
    .button-infotag {
      font-size: 14px;
      height: 36px;
      padding: 0 20px;
      border-radius: 18px;
      background-color: #ffff53;
      color: #333;
      display: inline-block;
      line-height: 36px;
      position: absolute;
      top: -10px;
      left: 0px;
      @media (max-width: 768px) {
        height: 30px;
        line-height: 30px;
        border-radius: 15px;
        top: -10px;
        left: 0px;
      }
    }
    &.button-disable {
      width: 100%;
      height: 48px;
      border-radius: 24px;
      display: inline-block;
      line-height: 48px;
      color: #ffffff;
      position: relative;
      font-size: 15px;
      text-align: center;
      pointer-events: none;
      transition: 0.3s ease;
      background-color: #989898;
      font-weight: bold;
      .button-infotag {
        background-color: #bab8b8;
      }
    }
  }
`

const PlanLayout = styled.div`
  padding: 0 20px 66px;
  @media (max-width: 768px) {
    padding: 0 15px 80px;
  }
  .layout-col2 {
    display: flex;
    justify-content: space-between;
    max-width: 1280px;
    margin: auto;
    @media (max-width: 768px) {
      display: block;
    }
  }
  .layout-col2-right {
    width: 27.8%;
    width: 356px;
    position: relative;
    @media (max-width: 1280px) {
      width: 280px;
    }
    @media (max-width: 768px) {
      width: 100%;
    }
  }
  .layout-col2-left {
    width: 69%;
    width: calc(100% - 396px);
    @media (max-width: 1280px) {
      width: calc(100% - 320px);
    }
    @media (max-width: 768px) {
      width: 100%;
    }
  }

  .plan-owner {
    background-color: #dae3e3;
    border-radius: 16px;
    margin-top: -44px;
    text-align: center;
    padding: 35px 24px;
    width: 356px;
    position: sticky;
    top: 120px;
    @media (max-width: 1280px) {
      width: 280px;
    }
    &.is-fixed {
      position: fixed;
      top: 90px;
      margin-top: 0;
      @media (max-width: 768px) {
        position: relative;
        top: 0;
        margin-top: 18px;
      }
    }
    &.is-stopped {
      position: absolute;
      bottom: 0;
      top: auto;
    }
    &.is-bottom {
      position: absolute;
      top: auto;
      bottom: 0;
      @media (max-width: 768px) {
        position: relative;
        margin-top: 18px;
      }
    }
    @media (max-width: 768px) {
      margin-top: 18px;
      width: 100%;
    }
    .plan-owner-head-text {
      font-size: 13px;
    }
    .plan-owner-image {
      width: 120px;
      height: 120px;
      border-radius: 60px;
      overflow: hidden;
      margin: 22px auto 0;
      background-size: cover;
      background-position: center;
    }
    .plan-owner-name {
      font-weight: bold;
      font-size: 16px;
      margin-top: 14px;
    }
    .plan-owner-text {
      margin-top: 12px;
      margin-bottom: 12px;
      font-size: 14px;
      text-align: left;
      font-weight: normal;
      @media (max-width: 768px) {
        padding: 0 15px;
      }
    }
    .plan-owner-button {
      margin-top: 24px;
      color: #26bfdb;
      width: 100%;
      font-size: 14px;
      height: 48px;
      border-radius: 24px;
      line-height: 48px;
      outline: 0px;
    }
  }

  .plan-list {
    margin-top: -46px;
    .plan-list-item {
      border-radius: 16px;
      background-color: #ffffff;
      box-shadow: 0 4px 24px rgba(107, 133, 141, 0.32);
      padding: 32px;
      @media (max-width: 768px) {
        overflow: hidden;
        padding: 0 0 30px;
      }
      &.is-secretPlan {
        padding: 0 32px 32px;
        border: 1px solid #000;
        @media (max-width: 768px) {
          overflow: hidden;
          padding: 0 0 30px;
        }
      }
      &.is-disable {
        background-color: #f6f7f7;
        .plan-list-bottom-box {
          .expanded {
            &:before {
              content: '';
              background: linear-gradient(
                0deg,
                rgb(246 247 247) 0%,
                rgb(246 247 247) 30%,
                rgba(107, 133, 141, 0) 100%
              );
            }
          }
        }
      }
      &.is-over {
        .plan-list-member-number {
          color: #f03b6b;
        }
      }
      & + .plan-list-item {
        margin-top: 35px;
      }
    }
    .plan-list-button {
      max-width: 374px;
      margin-top: 10px;
      @media (max-width: 768px) {
        max-width: 100%;
      }
    }
    .plan-list-secretPlan-header {
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: #000;
      color: #fff;
      padding: 12px 0 13px;
      border-radius: 10px 10px 0 0;
      margin: 0px -32px 32px;
      @media (max-width: 768px) {
        margin: 0;
      }
    }
    .plan-list-upper-box {
      display: flex;
      flex-direction: row-reverse;
      justify-content: space-between;
      @media (max-width: 768px) {
        display: block;
      }
      &.is-noimage {
        .plan-list-title-box {
          width: 100%;
        }
      }
    }
    .plan-list-image-box {
      width: 44%;
      @media (max-width: 768px) {
        width: 100%;
      }
      &.plan-image-none {
        display: none;
      }
    }
    .plan-list-image {
      width: 100%;
      background-size: cover;
      border-radius: 8px;
      overflow: hidden;
      position: relative;
      background-position: center;
      @media (max-width: 768px) {
        border-radius: 0;
      }
      &::before {
        content: '';
        display: block;
        padding-bottom: 53%;
      }
    }
    .plan-list-title-box {
      width: 50%;
      @media (max-width: 768px) {
        width: 100%;
        padding: 12px 16px 0;
      }
      &.plan-image-none {
        width: 100%;
      }
    }
    .plan-list-status {
      display: flex;
      align-items: center;
      font-weight: bold;
      font-size: 13px;
      position: relative;
      padding-left: 22px;
    }
    .plan-list-status-item {
      padding: 0 10px;
      & + .plan-list-status-item {
        border-left: 1px solid #dae3e3;
      }
    }
    .plan-list-member-number {
      margin-left: 4px;
    }
    .plan-list-user-icon {
      width: 24px;
      height: 24px;
      background-repeat: no-repeat;
      background-size: 100%;
      display: inline-block;
      position: absolute;
      left: 0;
    }
    .plan-list-text-small {
      font-weight: 400;
      font-size: 11px;
    }
    .plan-list-title {
      margin-top: 10px;
      font-weight: bold;
      font-size: 22px;
      line-height: 1.45;
      @media (max-width: 768px) {
        font-size: 16px;
      }
    }
    .plan-list-price {
      margin-top: 0px;
      font-size: 9px;
      color: #797979;
    }
    .plan-list-price-number {
      font-size: 36px;
      font-family: 'Barlow Semi Condensed';
      font-weight: bold;
      color: #333;
      margin: 0 5px;
    }
    .plan-list-bold {
      font-weight: bold;
      font-size: 15px;
      color: #333;
    }
    .plan-list-bottom-box {
      margin-top: 22px;
      @media (max-width: 768px) {
        padding: 0 16px;
      }
      .plan-list-caution-box {
        ul {
          list-style: disc;
          padding-left: 18px;
        }
      }
      .expanded {
        max-height: 217px;
        overflow: hidden;
        position: relative;
        @media (max-width: 768px) {
          max-height: 165px;
        }
        &:before {
          content: '';
          width: 100%;
          bottom: -14px;
          position: absolute;
          height: 64px;
          display: block;
          background: rgb(255, 255, 255);
          background: linear-gradient(
            0deg,
            rgb(255, 255, 255) 0%,
            rgb(255, 255, 255) 30%,
            rgba(255, 255, 255, 0) 100%
          );
        }
      }
    }
    .plan-list-paragraph {
      font-size: 14px;
      max-width: 100%;
      white-space: pre-wrap;
      font-weight: normal;
      & + .plan-list-paragraph {
        margin-top: 30px;
      }
      & + .plan-list-caution-box {
        margin-top: 32px;
      }
    }
    .plan-list-paragraph-more {
      display: flex;
      font-size: 13px;
      font-weight: bold;
      color: #26bfdb;
      text-align: center;
      transition: 0.3s ease;
      cursor: pointer;
      appearance: none;
      border: 0;
      background: transparent;
      padding: 0;
      margin: 32px auto 0;
      font-weight: normal;
      @media (max-width: 768px) {
        margin: 16px auto 0;
      }
    }
    .plan-list-caution-box {
      background-color: rgba(0, 0, 0, 0.05);
      border-radius: 8px;
      padding: 20px 16px;
      font-weight: normal;
      & + .plan-list-paragraph {
        margin-top: 30px;
      }
    }
    .plan-list-subtitle-red {
      font-size: 14px;
      font-weight: bold;
      color: #f03b6b;
      padding: 0 0 0 6px;
      border-left: 3px solid #f03b6b;
      line-height: 20px;
      margin-top: 10px;
    }
    .plan-list-text {
      margin-top: 10px;
      font-size: 14px;
    }
    .plan-list-text-red {
      font-weight: bold;
      color: #f03b6b;
    }
    .plan-list-price-before {
      font-family: 'Barlow Semi Condensed';
      font-weight: bold;
      color: #333;
      position: relative;
      display: inline-block;
      font-size: 20px;
      margin: 0 5px;
      &::before {
        content: '';
        height: 2px;
        width: 100%;
        background-color: #f03b6b;
        position: absolute;
        top: 0;
        bottom: 0;
        margin: auto;
      }
    }
    .plan-list-caution-title {
      font-weight: bold;
      margin-bottom: 15px;
    }
    .plan-list-price-note {
      font-size: 14px;
      @media (max-width: 768px) {
        font-size: 11px;
      }
    }
  }
`

const OtherPlansDiv = styled.div`
  margin-top: 64px;
  @media (max-width: 768px) {
    margin-top: 40px;
  }
  h3 {
    font-size: 22px;
    font-weight: bold;
    color: #333;
    margin-bottom: 32px;
    @media (max-width: 768px) {
      text-align: center;
      font-size: 16px;
    }
  }
  .plan-list {
    margin-top: 0;
  }
`
